import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, switchMap, map, delay, filter, merge } from 'rxjs';
import { ProactiveChatService } from '../../services/proactive-chat.service';
import { Store } from '@ngrx/store';
import { ProactiveChatActions } from './proactive-chat.actions';
import { VaButtonActions } from '../va-button/va-button.actions';
import { selectProactiveChatState } from './proactive-chat.reducer';
import {
  ChatWindowStates,
  FloatingButtonStates,
  Language,
} from '../../types/enum';
import { selectVaButtonState } from '../va-button/va-button.reducer';
import { selectSettingsState } from '../settings/settings.reducers';
import { selectWindowStateState } from '../window/window.reducer';
import { WindowMessagesActions } from '../window-messages/window-messages.actions';

@Injectable({
  providedIn: 'root',
})
export class ProactiveChatEffects {
  constructor(
    private actions$: Actions,
    private proactiveChatService: ProactiveChatService,
    private store: Store
  ) {}

  getProactiveChatConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VaButtonActions.enableOrDisableFloatingButton),
      concatLatestFrom(() => [
        this.store.select(selectSettingsState),
        this.store.select(selectWindowStateState),
      ]),
      filter(
        ([action, , windowState]) =>
          windowState !== ChatWindowStates.MINIMIZED &&
          windowState !== ChatWindowStates.OPENED &&
          action.isFloatingButtonEnabled
      ),
      switchMap(([, settingsState]) => {
        return this.proactiveChatService
          .getProactiveChatConfig(settingsState.language)
          .pipe(
            map((proactiveChatConfig) => {
              return ProactiveChatActions.checkProactiveConfig({
                proactiveChatConfig,
              });
            })
          );
      })
    )
  );

  checkProactiveConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProactiveChatActions.checkProactiveConfig),
      concatLatestFrom(() => this.store.select(selectProactiveChatState)),
      filter(([, proactiveChat]) => !!proactiveChat.engagements?.length),
      switchMap(([, proactiveChat]) => {
        return of(ProactiveChatActions.showProactive()).pipe(
          delay(proactiveChat.engagements[0].triggerAfter)
        );
      })
    )
  );

  showProactive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProactiveChatActions.showProactive),
      concatLatestFrom(() => [
        this.store.select(selectProactiveChatState),
        this.store.select(selectSettingsState),
        this.store.select(selectVaButtonState),
      ]),
      filter(
        ([, proactiveChat, , vaButton]) =>
          proactiveChat.engagements?.length > proactiveChat.currentIndex &&
          vaButton.floatingButtonState === FloatingButtonStates.FLOATING
      ),
      switchMap(([, proactiveChat, settingsState]) => {
        const currentEngagemnet =
          proactiveChat.engagements[proactiveChat.currentIndex];
        return merge(
          of(
            VaButtonActions.setProactiveChat({
              popOverText:
                settingsState.language === Language.fr
                  ? currentEngagemnet.contentTextFr
                  : currentEngagemnet.contentTextEn,
            })
          ),
          of(ProactiveChatActions.hideProactive()).pipe(
            delay(currentEngagemnet.displayDuration)
          )
        );
      })
    )
  );

  hideProactive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProactiveChatActions.hideProactive,
        WindowMessagesActions.initializeApp
      ),
      concatLatestFrom(() => [this.store.select(selectProactiveChatState)]),
      switchMap(([, proactiveChat]) => {
        if (proactiveChat.engagements.length > proactiveChat.currentIndex + 1) {
          const triggerAfter =
            proactiveChat.engagements[proactiveChat.currentIndex + 1]
              .triggerAfter;
          return merge(
            of(VaButtonActions.setProactiveChat({ popOverText: '' })),
            of(ProactiveChatActions.showProactive()).pipe(delay(triggerAfter))
          );
        } else {
          return of(VaButtonActions.setProactiveChat({ popOverText: '' }));
        }
      })
    )
  );
}
